import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const EditSiteModal = ({ site, isOpen, onClose, onUpdate }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  const [formData, setFormData] = useState({
    dns: site?.dns || '',
    sitemaps: site?.sitemaps || '',
    status: site?.status || 0,
    tarif: site?.tarif || 0,
    https: site?.https || 0,
    ymetrica: site?.ymetrica || 0,
    topvisor_project_id: site?.topvisor_project_id || '',
    topvisor_user_id: site?.topvisor_user_id || '', // Новое поле
    topvisor_api: '', // Никогда не светим старый ключ
    ymetrica_token: '', // Никогда не светим старый токен
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Обновляем данные формы при изменении сайта или открытии модального окна
  useEffect(() => {
    if (site && isOpen) {
      setFormData({
        dns: site.dns || '',
        sitemaps: site.sitemaps || '',
        status: site.status || 0,
        tarif: site.tarif || 0,
        https: site.https || 0,
        ymetrica: site.ymetrica || 0,
        topvisor_project_id: site.topvisor_project_id || '',
        topvisor_user_id: site.topvisor_user_id || '', // Новое поле
        topvisor_api: '', // Никогда не светим старый ключ
        ymetrica_token: '', // Никогда не светим старый токен
      });
      setError(null);
    }
  }, [site, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Не отправляем токены, если они не заполнены
      const dataToSend = { ...formData };
      if (!dataToSend.topvisor_api) delete dataToSend.topvisor_api;
      if (!dataToSend.ymetrica_token) delete dataToSend.ymetrica_token;
      if (!dataToSend.topvisor_user_id) delete dataToSend.topvisor_user_id;

      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${site.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(dataToSend),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showNotification(currentLang === 'ru' ? 'Сайт успешно обновлён!' : 'Site updated!', 'success');
        onClose();
        // Вызываем onUpdate с null, чтобы родительский компонент перезагрузил данные
        onUpdate(null);
      } else {
        setError(data.message || 'Ошибка обновления сайта');
      }
    } catch (error) {
      
      setError(error.message || 'Ошибка сети при обновлении сайта');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-bold">
              {currentLang === 'ru' ? 'Редактировать сайт' : 'Edit Site'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'DNS' : 'DNS'}
              </label>
              <input
                type="text"
                name="dns"
                value={formData.dns}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Sitemap URL' : 'Sitemap URL'}
              </label>
              <input
                type="text"
                name="sitemaps"
                value={formData.sitemaps}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Статус' : 'Status'}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                >
                  <option value={1}>{currentLang === 'ru' ? 'Активен' : 'Active'}</option>
                  <option value={0}>{currentLang === 'ru' ? 'Неактивен' : 'Inactive'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Тариф' : 'Tariff'}
                </label>
                <select
                  name="tarif"
                  value={formData.tarif}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                >
                  <option value={0}>{currentLang === 'ru' ? 'Базовый' : 'Basic'}</option>
                  <option value={1}>{currentLang === 'ru' ? 'Старт' : 'Start'}</option>
                  <option value={2}>{currentLang === 'ru' ? 'Стандарт' : 'Standard'}</option>
                  <option value={3}>{currentLang === 'ru' ? 'Бизнес' : 'Business'}</option>
                  <option value={4}>{currentLang === 'ru' ? 'VIP' : 'VIP'}</option>
                </select>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="https"
                  checked={formData.https === 1}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 focus:ring-2"
                />
                <label className="ml-2 text-sm text-gray-700">
                  HTTPS
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {currentLang === 'ru' ? 'Яндекс.Метрика (номер счетчика)' : 'Yandex Metrica (counter ID)'}
              </label>
              <input
                type="text"
                name="ymetrica"
                value={formData.ymetrica}
                onChange={handleInputChange}
                placeholder={currentLang === 'ru' ? 'Введите ID счетчика' : 'Enter counter ID'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Яндекс.Метрика API Token
              </label>
              {site?.ymetrica_token ? (
                <div className="mb-2 text-green-700 text-xs">{currentLang === 'ru' ? 'Токен задан' : 'Token is set'}</div>
              ) : (
                <div className="mb-2 text-red-700 text-xs">{currentLang === 'ru' ? 'Токен не задан' : 'Token is not set'}</div>
              )}
              <input
                type="text"
                name="ymetrica_token"
                value={formData.ymetrica_token}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                placeholder={currentLang === 'ru' ? 'Введите новый токен (опционально)' : 'Enter new token (optional)'}
                autoComplete="off"
              />
              <div className="text-xs text-gray-500 mt-1">
                {currentLang === 'ru' ? 'Если вы хотите заменить токен, введите новый. Оставьте поле пустым, чтобы не менять.' : 'If you want to replace the token, enter a new one. Leave blank to keep current.'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topvisor Project ID
              </label>
              <input
                type="text"
                name="topvisor_project_id"
                value={formData.topvisor_project_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                placeholder={currentLang === 'ru' ? 'ID проекта Topvisor' : 'Topvisor Project ID'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topvisor User ID
              </label>
              <input
                type="text"
                name="topvisor_user_id"
                value={formData.topvisor_user_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                placeholder={currentLang === 'ru' ? 'ID пользователя Topvisor' : 'Topvisor User ID'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topvisor API
              </label>
              {site?.topvisor_api ? (
                <div className="mb-2 text-green-700 text-xs">{currentLang === 'ru' ? 'API-ключ задан' : 'API key is set'}</div>
              ) : (
                <div className="mb-2 text-red-700 text-xs">{currentLang === 'ru' ? 'API-ключ не задан' : 'API key is not set'}</div>
              )}
              <input
                type="text"
                name="topvisor_api"
                value={formData.topvisor_api}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                placeholder={currentLang === 'ru' ? 'Введите новый API-ключ (опционально)' : 'Enter new API key (optional)'}
                autoComplete="off"
              />
              <div className="text-xs text-gray-500 mt-1">
                {currentLang === 'ru' ? 'Если вы хотите заменить API-ключ, введите новый. Оставьте поле пустым, чтобы не менять.' : 'If you want to replace the API key, enter a new one. Leave blank to keep current.'}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-3 sm:px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-3 sm:px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors text-sm"
              >
                {loading ? (currentLang === 'ru' ? 'Сохранение...' : 'Saving...') : (currentLang === 'ru' ? 'Сохранить' : 'Save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditSiteModal; 